import { css } from '@emotion/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';

import { Typography } from '@/components/typography';
import { cssProps } from '@/theme/theme';
import { cssPropVar, typography } from '@/theme/utils';
import { useConsentOptions, useGiveConsent, useIsConsentPending } from '../hooks';
import { PrimaryButton, SecondaryButton } from './Buttons';

const ConsentPopupContent = () => {
	const consentOptions = useConsentOptions();
	const giveConsent = useGiveConsent();

	const [selectedOptions, setSelectedOptions] = useState<Record<string, boolean>>({});

	useEffect(() => {
		if (!consentOptions) {
			return;
		}
		setSelectedOptions(consentOptions.options.reduce((prev, curr) => ({ ...prev, [curr.value]: !curr.optIn }), {}));
	}, [consentOptions]);

	if (!consentOptions) {
		return null;
	}

	const { text, options } = consentOptions;

	const accept = () => giveConsent(selectedOptions);
	const acceptAll = () => giveConsent(options.reduce<Record<string, boolean>>((prev, curr) => ({ ...prev, [curr.value]: true }), {}));

	return (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			exit={{ opacity: 0 }}
			css={css`
				box-sizing: border-box;
				position: fixed;
				bottom: 1em;
				right: 1em;
				background: ${cssPropVar(cssProps.color.bg)};
				box-shadow: 0 0 2px ${cssPropVar(cssProps.color.fg)};
				color: ${cssPropVar(cssProps.color.fg)};
				padding: 1em;
				width: calc(100vw - 2em);
				max-width: ${500 / 16}em;
			`}
			role="dialog"
			aria-modal="false"
			aria-labelledby="consent-title"
			aria-describedby="consent-message"
		>
			<section>
				<div>
					<Typography
						as="h2"
						id="consent-title"
						size="m"
						css={css`
							margin: 0;
						`}
					>
						{text.title}
					</Typography>
					<Typography
						as="p"
						id="consent-message"
						size="s"
						css={css`
							margin: 0;
						`}
					>
						{text.message}
					</Typography>
					<Typography
						as="a"
						css={[
							typography({ size: 's' }),
							css`
								color: currentColor;
								text-decoration: underline;
							`,
						]}
						href={text.privacyLink}
					>
						{text.privacyLinkLabel}
					</Typography>
				</div>
				<div
					css={css`
						display: flex;
						flex-direction: column;
						gap: 0.25em;
						list-style: none;
						margin: 0;
						padding: 0;
					`}
				>
					<div
						css={css`
							margin: 0;
						`}
					>
						<Typography as="span" size="s" font="sansSerif">
							{text.types}
						</Typography>
					</div>
					<ul
						css={css`
							display: flex;
							flex-direction: column;
							gap: 0.1em;
							list-style: none;
							margin: 0;
							padding: 0;
						`}
					>
						{options.map((o) => (
							<li
								key={o.value}
								css={css`
									list-style: none;
								`}
							>
								<label
									css={css`
										display: flex;
										align-items: center;
										gap: 1em;
									`}
								>
									<div
										css={css`
											position: relative;
											width: 1.25em;
											height: 1em;
											overflow: hidden;
											display: flex;
											align-items: center;
											justify-content: flex-start;
										`}
									>
										<input
											disabled={!o.optIn}
											onChange={(e) => {
												const newVal = e.target.checked;
												setSelectedOptions((prev) => ({ ...prev, [o.value]: newVal }));
											}}
											type="checkbox"
											checked={selectedOptions[o.value] === undefined ? true : selectedOptions[o.value]}
										/>
									</div>
									<Typography as="span" size="s" font="sansSerif">
										{o.label}
									</Typography>
								</label>
								<div
									css={css`
										margin-left: 2.25em;
									`}
								>
									<Typography
										as="p"
										size="s"
										font="sansSerif"
										css={css`
											margin: 0;
										`}
									>
										{o.description}
									</Typography>
								</div>
							</li>
						))}
					</ul>
				</div>
				<div
					css={css`
						padding-top: 0.5em;
					`}
				>
					<div
						css={css`
							display: flex;
							gap: 1em;
							flex-direction: column;
							align-items: center;
							@media (min-width: ${400 / 16}em) {
								flex-direction: row;
								justify-content: space-between;
							}
						`}
					>
						<PrimaryButton onClick={acceptAll}>{text.acceptAll}</PrimaryButton>
						<SecondaryButton onClick={accept}>{text.acceptSelection}</SecondaryButton>
					</div>
				</div>
			</section>
		</motion.div>
	);
};

const ConsentPopup = () => {
	const isConsentPending = useIsConsentPending();

	return <AnimatePresence>{isConsentPending && <ConsentPopupContent />}</AnimatePresence>;
};

export default ConsentPopup;
